



























































import { PropType } from 'vue';
import axios from 'axios';
import EventBus from '../../../eventBus';
import { getOnlineUserIds } from '../../../utils/realtimePresence';
import CoSkeleton from '../../Atoms/co-skeleton/CoSkeleton.vue';
import CoIcon from '../../Atoms/co-icon/CoIcon.vue';
import CoText from '../../Atoms/co-text/CoText.vue';
import CoRoundButton from '../../Atoms/co-round-button/CoRoundButton.vue';
import CoUser from '../../Molecules/co-user/CoUser.vue';
import CoUserInvite from '../co-user-invite/CoUserInvite.vue';

interface PresenceUpdate {
    userId: string;
    online: boolean;
    changedAt?: number | string | null;
}

interface WidgetMemberObject {
    ID?: string | number;
    Status?: number | string;
    LastActivity?: number | string;
    Profile?: Record<string, unknown>;
    [key: string]: unknown;
}

interface WidgetMember {
    object: WidgetMemberObject;
    type?: string;
    [key: string]: unknown;
}

export default {
    name: 'CoActiveMembersWidget',
    components: {
        CoSkeleton,
        CoIcon,
        CoText,
        CoRoundButton,
        CoUserInvite,
        CoUser,
    },
    props: {
        asAdmin: {
            type: Boolean as PropType<boolean>,
            required: false,
            default: false,
        },
    },
    data: () => ({
        loading: true,
        members: [] as WidgetMember[],
        activeMembers: [] as WidgetMember[],
        presenceUpdateTimeout: null,
        pendingPresenceUpdates: new Map<string, PresenceUpdate>(),
    }),

    mounted() {
        EventBus.$on('USER_PRESENCE_UPDATED', this.handlePresenceUpdated);
        this.listMembers();
    },
    beforeDestroy() {
        EventBus.$off('USER_PRESENCE_UPDATED', this.handlePresenceUpdated);
        if (this.presenceUpdateTimeout) {
            clearTimeout(this.presenceUpdateTimeout);
        }
    },
    methods: {
        goToMembers() {
            this.$emit('goToMembers');
        },
        isAvailableMember(member: WidgetMember | null) {
            return Number(member?.object?.Status) !== -1;
        },
        dedupeMembersById(members: WidgetMember[] = []) {
            const seenUserIds = new Set();

            return members.filter((member) => {
                if (!this.isAvailableMember(member)) return false;

                const userId = String(member?.object?.ID || '');
                if (!userId || seenUserIds.has(userId)) return false;

                seenUserIds.add(userId);
                return true;
            });
        },
        refreshVisibleMembers() {
            this.activeMembers = this.dedupeMembersById(this.activeMembers);
            this.members = this.activeMembers.slice(0, 4);
        },
        coUserFromMember(member: WidgetMember) {
            const user = member?.object || {};

            // CoUser expects profile display fields at the top level, while the widget API nests them under Profile.
            return {
                ...user,
                ...(user.Profile || {}),
            };
        },
        async listMembersByIds(userIds: Array<string | number> = []): Promise<WidgetMember[]> {
            const uniqueUserIds = Array.from(new Set(userIds.map((userId) => String(userId)).filter(Boolean)));
            if (uniqueUserIds.length === 0) return [];

            const ids = JSON.stringify({ IDS: uniqueUserIds.map((ID) => ({ ID })) });
            const response = await this.$store.dispatch('listUsersByIDs', { ids });
            const users = response?.Users || [];

            // Keep the same row shape as /user/profile/list so rendering and de-duplication stay simple.
            return users.map((user) => ({
                object: user,
                type: 'user',
            }));
        },
        normalizePresenceTimestamp(changedAt, fallbackTimestamp: number | string | null = null) {
            const normalizedTimestamp = Number(changedAt);

            if (Number.isFinite(normalizedTimestamp) && normalizedTimestamp > 0) {
                return normalizedTimestamp;
            }

            return fallbackTimestamp;
        },
        indexAfterOnlineMembers(members: WidgetMember[]) {
            const onlineUserIds = new Set(getOnlineUserIds().map(String));

            return members.findIndex((member) => !onlineUserIds.has(String(member?.object?.ID)));
        },
        mergeOnlineAndActiveMembers(onlineMembers: WidgetMember[], activeMembers: WidgetMember[]) {
            const activeMembersById = new Map<string, WidgetMember>(
                activeMembers
                    .filter((member) => member?.object?.ID && this.isAvailableMember(member))
                    .map((member) => [String(member.object.ID), member])
            );
            const prioritizedOnlineMembers = onlineMembers
                .filter((member) => this.isAvailableMember(member))
                .map((member) => {
                    const existingMember: WidgetMember | null =
                        activeMembersById.get(String(member?.object?.ID)) || null;

                    return {
                        ...(existingMember || {}),
                        ...member,
                        object: {
                            ...((existingMember && existingMember.object) || {}),
                            ...(member.object || {}),
                        },
                    };
                });

            return this.dedupeMembersById([...prioritizedOnlineMembers, ...activeMembers]);
        },
        applyPresenceUpdateToMembers(
            members: WidgetMember[],
            presenceUpdate: PresenceUpdate,
            fetchedMembersById: Map<string, WidgetMember> = new Map<string, WidgetMember>()
        ) {
            const normalizedUserId = String(presenceUpdate?.userId || '');
            if (!normalizedUserId) return members;

            const existingIndex = members.findIndex((member) => String(member?.object?.ID) === normalizedUserId);
            const existingMember = existingIndex >= 0 ? members[existingIndex] : null;
            const fetchedMember = fetchedMembersById.get(normalizedUserId) || null;
            let member = null;

            if (existingMember) {
                member = {
                    ...existingMember,
                    object: {
                        ...(existingMember.object || {}),
                    },
                };
            } else if (fetchedMember) {
                member = {
                    ...fetchedMember,
                    object: {
                        ...(fetchedMember.object || {}),
                    },
                };
            }

            if (!member) {
                return members;
            }

            const nextMembers = [...members];
            if (existingIndex >= 0) {
                nextMembers.splice(existingIndex, 1);
            }

            if (!this.isAvailableMember(member)) {
                return nextMembers;
            }

            const lastActivity = this.normalizePresenceTimestamp(
                presenceUpdate?.changedAt,
                member?.object?.LastActivity || null
            );
            if (lastActivity !== null) {
                member.object.LastActivity = lastActivity;
            }

            if (presenceUpdate?.online) {
                // Keep the most recently active online member at the top of the widget source list.
                nextMembers.unshift(member);
                return nextMembers;
            }

            // Offline members stay below the online block, newest offline first.
            const firstOfflineIndex = this.indexAfterOnlineMembers(nextMembers);
            const insertionIndex = firstOfflineIndex === -1 ? nextMembers.length : firstOfflineIndex;
            nextMembers.splice(insertionIndex, 0, member);

            return nextMembers;
        },
        async flushPresenceUpdates() {
            const queuedPresenceUpdates: PresenceUpdate[] = Array.from(this.pendingPresenceUpdates.values());
            this.pendingPresenceUpdates.clear();

            if (queuedPresenceUpdates.length === 0) {
                return;
            }

            const missingOnlineUserIds = queuedPresenceUpdates
                .filter((update) => update?.online)
                .map((update) => String(update?.userId || ''))
                .filter(
                    (userId) => userId && !this.activeMembers.some((member) => String(member?.object?.ID) === userId)
                );
            const fetchedMembers = await this.listMembersByIds(missingOnlineUserIds);
            const fetchedMembersById = new Map<string, WidgetMember>(
                fetchedMembers
                    .filter((member) => member?.object?.ID)
                    .map((member) => [String(member.object.ID), member])
            );

            let nextMembers = [...this.activeMembers];

            queuedPresenceUpdates.forEach((update) => {
                nextMembers = this.applyPresenceUpdateToMembers(nextMembers, update, fetchedMembersById);
            });

            this.activeMembers = nextMembers;
            this.refreshVisibleMembers();
        },
        async listMembers() {
            this.loading = true;

            try {
                const onlineMembers = await this.listOnlineMembers();
                const response = await axios.get('/user/profile/list');
                this.activeMembers = this.mergeOnlineAndActiveMembers(onlineMembers, response?.data?.objects || []);
                this.refreshVisibleMembers();
            } catch (error) {
                console.log(error);
                this.activeMembers = this.mergeOnlineAndActiveMembers([], this.activeMembers);
                this.refreshVisibleMembers();
            } finally {
                this.loading = false;
            }
        },
        async listOnlineMembers() {
            const onlineUserIds = getOnlineUserIds().slice(0, 4);
            return this.listMembersByIds(onlineUserIds);
        },
        handlePresenceUpdated(presenceUpdate: PresenceUpdate) {
            if (!presenceUpdate?.userId) return;

            this.pendingPresenceUpdates.set(String(presenceUpdate.userId), presenceUpdate);

            if (this.presenceUpdateTimeout) {
                clearTimeout(this.presenceUpdateTimeout);
            }

            // Batch bursty snapshot updates into one user lookup before reordering the widget source list.
            this.presenceUpdateTimeout = window.setTimeout(() => {
                this.presenceUpdateTimeout = null;
                this.flushPresenceUpdates().catch((error) => {
                    console.log(error);
                });
            }, 100);
        },
    },
};
